import { Button } from 'components/Button';
import { ButtonGroup } from 'components/ButtonGroup';
import { PageToolbar } from 'components/PageToolbar';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Menu } from 'components/Menu';
import { ModalScreens } from 'components/Navigation/types';
import { PageHeader } from 'components/PageHeader';
import { getBreakpoint } from 'hooks/useScreenBreakpoints';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { pluralise } from 'lib/pluralise';
import { randomKey } from 'lib/riskbase';
import { sortBy } from 'lodash';
import React from 'react';
import { LayoutChangeEvent } from 'react-native';

import { useDispatch } from 'react-redux';
import {
  doAsesListLoad,
  doAsesListFilterOpenModal,
  doAsesListFilterOpenOrganisationSelect,
  doAsesListSetFilter,
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
} from 'store/actions';
import {
  selectAsesListFilter,
  selectAsesListFilterOptions,
  selectAsesListFilterSearchTerm,
  selectAsesListFilterStatuses,
  selectAsesListTotalCount,
  selectAsesListFilterEverLoaded,
  selectAsesListEverLoaded,
} from 'store/ases/selectors';
import { ListFilter } from 'store/ases/types';
import { AssessmentStatusesOrder } from 'types/types';

const screenBreakpoints = [
  {
    width: 400,
    key: 1,
  },
  {
    width: 450,
    key: 2,
  },
  {
    width: 550,
    key: 3,
  },
  {
    width: 700,
    key: 4,
  },
  {
    width: 850,
    key: 5,
  },
];

export const AssessmentListHeader: React.FC = () => {
  const dispatch = useDispatch();

  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const listEverLoaded = useSelector(selectAsesListEverLoaded);
  const isListFilterLoaded = useSelector(selectAsesListFilterEverLoaded);
  const listTotalCount = useSelector(selectAsesListTotalCount);
  const searchValue = useSelector(selectAsesListFilterSearchTerm);
  const filterOptions = useSelector(selectAsesListFilterOptions);
  const filterValues = useSelector(selectAsesListFilter);
  const statusesValue = useSelector(selectAsesListFilterStatuses);
  const searchValueRef = React.useRef(searchValue);

  const handleFilter = React.useCallback(
    (filterChanges: Partial<ListFilter>) => {
      dispatch(
        doAsesListSetFilter({
          data: { ...filterChanges },
          append: true,
          loadList: {
            useBlockingLoader: false,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleSearch = React.useCallback(
    (newSearchValue: string) => {
      searchValueRef.current = newSearchValue;
      handleFilter({ searchTerm: newSearchValue });
    },
    [handleFilter],
  );

  // Check if search value has changed, provide a new key if it has
  const textfieldKeyRef = React.useRef(randomKey());
  if (searchValueRef.current !== searchValue) {
    textfieldKeyRef.current = randomKey();
    searchValueRef.current = searchValue;
  }

  const statusesText = React.useMemo<string>(() => {
    const thsStatusesText: string[] = filterOptions.statuses
      .filter((s) => statusesValue.includes(s.id))
      .map((s) => s.name);
    return thsStatusesText.length &&
      thsStatusesText.length !== AssessmentStatusesOrder.length
      ? `${thsStatusesText[0]}` +
          `${pluralise(thsStatusesText.length - 1, '+1', '+{x}', '')}`
      : '';
  }, [filterOptions.statuses, statusesValue]);

  const handleOpenAssetGroups = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: 'small',
        params: {
          title: 'Property Groups',
          options: filterOptions.propertyGroups.map((o) => ({
            internalValue: o.id,
            name: o.name,
            groupName: o.organisationName,
          })),
          multiSelect: true,
          searchable: true,
          showQuickChoose: true,
          eventKey: 'asesListHeaderFilterPropertyGroups',
          value: filterValues.propertyGroupIds,
        },
      }),
    );

  const handleOpenAssessmentTypes = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: 'small',
        params: {
          title: 'Assessment Types',
          options: sortBy(filterOptions.assessmentTypes, 'name').map((o) => ({
            internalValue: o.id,
            name: o.name,
          })),
          multiSelect: true,
          searchable: true,
          showQuickChoose: true,
          eventKey: 'asesListHeaderFilterAssessmentTypes',
          value: filterValues.assessmentTypes,
        },
      }),
    );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (action) => {
        if (action.payload.key === 'asesListHeaderFilterAssessmentTypes') {
          handleFilter({
            assessmentTypes: action.payload.stringValues,
          });
        } else if (
          action.payload.key === 'asesListHeaderFilterPropertyGroups'
        ) {
          handleFilter({
            propertyGroupIds: action.payload.stringValues,
          });
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch, handleFilter]);

  const [breakpoint, setBreakpoint] = React.useState<number>(1);
  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) =>
      setBreakpoint(
        getBreakpoint(screenBreakpoints, event.nativeEvent.layout.width) || 1,
      ),
    [],
  );

  return (
    <>
      <PageHeader
        title="Assessments"
        subtitle={
          listEverLoaded
            ? pluralise(listTotalCount, '{x} Assessment', '{x} Assessments')
            : ''
        }
        showSidebarButton
      />
      <PageToolbar
        searchValue={searchValue}
        onSearch={handleSearch}
        searchPlaceholder={
          filterOptions.organisationRequired
            ? 'Filter to enable search'
            : 'Search Assessments'
        }
        searchDisabled={filterOptions.organisationRequired}
        onLayout={handleLayout}
        searchOnSubmit
      >
        <ButtonGroup>
          {isListFilterLoaded && breakpoint > 1 && (
            <Menu
              buttonConfig={{
                text: statusesText || 'All Statuses',
              }}
              width={140}
              options={[
                ...filterOptions.statuses.map((status) => ({
                  text: status.name,
                  icon: ICONS.FileDuotone,
                  iconColor: `#${status.colour}`,
                  onPress: () => {
                    handleFilter({ statuses: [status.id] });
                  },
                })),
                '-',
                {
                  text: 'All Statuses',
                  onPress: () => {
                    handleFilter({ statuses: [] });
                  },
                },
              ]}
            />
          )}
          {isListFilterLoaded &&
            breakpoint > 2 &&
            filterOptions.organisations.length > 1 && (
              <Button
                text={`${
                  filterValues.organisationIds.length === 0
                    ? 'All Orgs'
                    : filterValues.organisationIds.length === 1
                      ? `${
                          filterOptions.organisations.find(
                            (org) => filterValues.organisationIds[0] === org.id,
                          )?.name
                        }`
                      : `${filterValues.organisationIds.length} Orgs`
                }`}
                style={{ maxWidth: 150 }}
                onPress={() =>
                  dispatch(
                    doAsesListFilterOpenOrganisationSelect({
                      getDraft: false,
                      openFilterAfterSelect: true,
                    }),
                  )
                }
              />
            )}
          {isListFilterLoaded &&
            breakpoint > 3 &&
            filterOptions.assessmentTypes.length > 1 && (
              <Button
                text={`${
                  filterValues.assessmentTypes.length === 0
                    ? 'All Types'
                    : filterValues.assessmentTypes.length === 1
                      ? `${
                          filterOptions.assessmentTypes.find(
                            (org) => filterValues.assessmentTypes[0] === org.id,
                          )?.name
                        }`
                      : `${filterValues.assessmentTypes.length} Types`
                }`}
                style={{ maxWidth: 150 }}
                onPress={handleOpenAssessmentTypes}
              />
            )}
          {isListFilterLoaded &&
            breakpoint > 4 &&
            filterOptions.propertyGroups?.length > 1 && (
              <Button
                text={`${
                  filterValues.propertyGroupIds.length === 0
                    ? 'All Groups'
                    : filterValues.propertyGroupIds.length === 1
                      ? `${
                          filterOptions.propertyGroups.find(
                            (assetGroup) =>
                              filterValues.propertyGroupIds[0] ===
                              assetGroup.id,
                          )?.name
                        }`
                      : `${filterValues.propertyGroupIds.length} Groups`
                }`}
                style={{ maxWidth: 150 }}
                onPress={handleOpenAssetGroups}
              />
            )}
          <IconButton
            icon={ICONS.RotateRightLight}
            onPress={() =>
              dispatch(
                doAsesListLoad({
                  append: false,
                  useDraftFilter: false,
                  useBlockingLoader: false,
                  type: 'filter',
                }),
              )
            }
          />
          <IconButton
            icon={ICONS.SlidersUpLight}
            onPress={() =>
              dispatch(doAsesListFilterOpenModal({ setDraft: false }))
            }
            showBadge={
              !!filterValues.organisationIds.length ||
              !!filterValues.assessmentTypes.length ||
              !!filterValues.propertyGroupIds.length ||
              !!filterValues.flagsSet.length ||
              !!filterValues.flagsNotSet.length ||
              !!statusesText
            }
          />
        </ButtonGroup>
      </PageToolbar>
    </>
  );
};
